import Phaser from 'phaser';
import { GAME_WIDTH } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { RENDER_SCALE } from '../renderScale';

/**
 * Color palettes for different winners
 */
const CONFETTI_COLORS = {
  // Team colors
  teamA: [PERSIAN_THEME.colors.teamA, 0xffd700, PERSIAN_THEME.colors.gold], // Gold shades
  teamB: [PERSIAN_THEME.colors.teamB, 0x4ecdc4, 0x6ab0c4], // Teal shades
  // Individual player colors
  player1: [0x4ecdc4, 0x3ebdb4, 0x5eddd4], // Teal
  player2: [0xff6b6b, 0xef5b5b, 0xff7b7b], // Coral
  player3: [0xc77dff, 0xb76def, 0xd78dff], // Purple
};

export type ConfettiColorKey = keyof typeof CONFETTI_COLORS;

/**
 * Celebratory confetti particle emitter for victory screens.
 * Creates a burst effect followed by continuous gentle stream.
 */
export class ConfettiEmitter {
  private scene: Phaser.Scene;
  private emitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private colors: number[];
  private textureKey: string;

  constructor(scene: Phaser.Scene, colorKey: ConfettiColorKey) {
    this.scene = scene;
    this.colors = CONFETTI_COLORS[colorKey] || CONFETTI_COLORS.teamA;
    this.textureKey = `confetti-particle-${Date.now()}`;
    this.createTexture();
  }

  /**
   * Generate rectangular confetti texture
   */
  private createTexture(): void {
    if (this.scene.textures.exists(this.textureKey)) return;

    const width = Math.ceil(8 * RENDER_SCALE);
    const height = Math.ceil(12 * RENDER_SCALE);
    const graphics = this.scene.make.graphics({});
    graphics.fillStyle(0xffffff);
    graphics.fillRect(0, 0, width, height);
    graphics.generateTexture(this.textureKey, width, height);
    graphics.destroy();
  }

  /**
   * Initial burst of confetti from the top
   */
  burst(): void {
    if (this.emitter) {
      this.emitter.destroy();
    }

    this.emitter = this.scene.add.particles(GAME_WIDTH / 2, -20, this.textureKey, {
      // Emit from full width at top
      x: { min: -GAME_WIDTH / 2, max: GAME_WIDTH / 2 },
      y: { min: -30, max: 0 },

      // Fall down with gravity
      speedY: { min: 100, max: 300 },
      speedX: { min: -80, max: 80 },
      gravityY: 200,

      // Rotation and wobble
      rotate: { min: 0, max: 360 },
      // Use angular velocity for spin effect
      frequency: -1, // Manual emission only for burst

      lifespan: { min: 3000, max: 5000 },
      scale: { start: 0.6 / RENDER_SCALE, end: 0.3 / RENDER_SCALE },
      alpha: { start: 0.9, end: 0 },

      tint: this.colors,
      blendMode: Phaser.BlendModes.ADD,
    });

    // Burst 200+ particles
    this.emitter.explode(250);

    // After burst, switch to continuous stream
    this.scene.time.delayedCall(500, () => {
      this.startContinuous();
    });
  }

  /**
   * Start continuous gentle stream of confetti
   */
  private startContinuous(): void {
    if (!this.emitter) return;

    // Reconfigure for continuous emission
    this.emitter.setConfig({
      x: { min: -GAME_WIDTH / 2, max: GAME_WIDTH / 2 },
      y: { min: -30, max: 0 },
      speedY: { min: 80, max: 200 },
      speedX: { min: -60, max: 60 },
      gravityY: 150,
      rotate: { min: 0, max: 360 },
      frequency: 80, // Emit every 80ms
      quantity: 2,
      lifespan: { min: 4000, max: 6000 },
      scale: { start: 0.5 / RENDER_SCALE, end: 0.2 / RENDER_SCALE },
      alpha: { start: 0.8, end: 0 },
      tint: this.colors,
      blendMode: Phaser.BlendModes.ADD,
    });

    this.emitter.start();
  }

  /**
   * Stop emitting (particles already in flight will complete)
   */
  stop(): void {
    this.emitter?.stop();
  }

  /**
   * Clean up emitter and texture
   */
  destroy(): void {
    if (this.emitter) {
      this.emitter.destroy();
      this.emitter = null;
    }
    // Clean up texture
    if (this.scene.textures.exists(this.textureKey)) {
      this.scene.textures.remove(this.textureKey);
    }
  }
}
